import axios from 'axios';
import {
  acquireRefreshLock,
  getAccessToken,
  getRefreshToken,
  releaseRefreshLock,
  REFRESH_LOCK_TTL_MS,
  waitForAccessToken,
} from '../auth/tokenStorage';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '',
});

let _getAccessToken: () => string | null = getAccessToken;
let _getRefreshToken: () => string | null = () => null;
let _setTokens: (at: string, rt: string) => void = () => {};
let _logout: () => void = () => {};

export function configureInterceptors(
  getAccessTokenFn: () => string | null,
  getRefreshTokenFn: () => string | null,
  setTokens: (at: string, rt: string) => void,
  logout: () => void
) {
  _getAccessToken = getAccessTokenFn;
  _getRefreshToken = getRefreshTokenFn;
  _setTokens = setTokens;
  _logout = logout;
}

apiClient.interceptors.request.use(cfg => {
  const at = _getAccessToken();
  if (at) cfg.headers.Authorization = `Bearer ${at}`;
  return cfg;
});

let refreshing: Promise<string> | null = null;

/** How many rounds a waiting tab gives the holder before failing instead of racing it. */
const REFRESH_LOCK_ATTEMPTS = 3;

/**
 * Returns a token another tab published, or null once this tab owns the lock and should refresh
 * itself.
 *
 * Waiting out the TTL is not by itself permission to proceed: the holder may just be slow, and
 * refreshing anyway would submit the same rotating refresh token twice. Each round therefore
 * tries to take the lock again, which only succeeds once the previous one has expired.
 */
async function acquireOrAwaitRefresh(staleAccessToken: string | null): Promise<string | null> {
  for (let attempt = 0; attempt < REFRESH_LOCK_ATTEMPTS; attempt++) {
    if (acquireRefreshLock()) return null;

    const shared = await waitForAccessToken(staleAccessToken, REFRESH_LOCK_TTL_MS);
    if (shared) return shared;
  }
  throw new Error('Another tab is still refreshing the session');
}

/**
 * Refreshes once per browser, not once per tab: the server rotates the refresh
 * token and stores a single hash per user, so parallel refreshes would log each
 * other out. The tab that wins the lock refreshes; the others wait for it to
 * publish the new access token and reuse that.
 */
async function runRefresh(staleAccessToken: string | null): Promise<string> {
  const shared = await acquireOrAwaitRefresh(staleAccessToken);
  if (shared) return shared;

  try {
    const rt = _getRefreshToken();
    if (!rt) throw new Error('No refresh token');
    const res = await axios.post(
      `${import.meta.env.VITE_API_URL ?? ''}/api/auth/refresh`,
      { refreshToken: rt }
    );
    _setTokens(res.data.accessToken, res.data.refreshToken);
    return res.data.accessToken as string;
  } catch (err) {
    // Another tab may have rotated the token while this request was in flight. Ending the
    // session then would throw away credentials that are perfectly good.
    const current = _getAccessToken();
    if (current && current !== staleAccessToken) return current;

    _logout();
    throw err;
  } finally {
    releaseRefreshLock();
  }
}

apiClient.interceptors.response.use(
  r => r,
  async err => {
    const original = err.config;
    if (err.response?.status !== 401 || original._retry) {
      return Promise.reject(err);
    }
    original._retry = true;

    const sentWith =
      typeof original.headers?.Authorization === 'string'
        ? original.headers.Authorization.replace(/^Bearer /, '')
        : null;

    // Another tab may have refreshed while this request was in flight.
    const current = _getAccessToken();
    if (current && current !== sentWith) {
      original.headers.Authorization = `Bearer ${current}`;
      return apiClient(original);
    }

    if (!_getRefreshToken()) {
      _logout();
      return Promise.reject(err);
    }

    if (!refreshing) {
      refreshing = runRefresh(sentWith).finally(() => {
        refreshing = null;
      });
    }

    const newAt = await refreshing;
    original.headers.Authorization = `Bearer ${newAt}`;
    return apiClient(original);
  }
);
